import { useState } from "react";
import { createPortal } from "react-dom";
import styles from "./AppTooltip.module.scss";
import { classNames } from "primereact/utils";
import { PortalTooltip } from "./PortalTooltip";
import { useSafeId } from "./hooks/useSafeId";
import { IconType } from "../../icons/Icon";
import {
  IfFalse,
  IfTrue,
  RenderWhen,
} from "../../infrastructure/components/RenderWhen";
import { IconButton } from "../button/iconButton/IconButton";

interface IAppTooltipProps {
  content: string | React.ReactNode;
  title?: string;
  className?: string;
  color?: string;
  iconType?: IconType;
  iconClassName?: string;
}

export const AppTooltip = ({
  content,
  title,
  color = "#fff",
  className,
  iconType,
  iconClassName,
}: IAppTooltipProps) => {
  const tooltipId = useSafeId();
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  if (!content) {
    return;
  }

  const openSheet = () => {
    setIsClosing(false);
    setIsOpen(true);
  };

  const closeSheet = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 280);
  };

  const renderedContent =
    typeof content === "string" ? (
      <div dangerouslySetInnerHTML={{ __html: content }} />
    ) : (
      content
    );

  return (
    <div className={classNames(styles.root, className)}>
      <RenderWhen condition={!iconType}>
        <IfTrue>
          <span
            className={tooltipId}
            style={{ cursor: "pointer" }}
            onClick={openSheet}
          >
            <i className="pi pi-info-circle" style={{ color }} />
          </span>
        </IfTrue>
        <IfFalse>
          <IconButton
            className={classNames(tooltipId, iconClassName)}
            iconType={iconType!}
            onClick={openSheet}
          />
        </IfFalse>
      </RenderWhen>

      {/* Desktop/Tablet: react-tooltip via portal */}
      <PortalTooltip
        anchorSelect={`.${tooltipId}`}
        place="top"
        className={styles.root_tooltip}
        positionStrategy="fixed"
      >
        <div dangerouslySetInnerHTML={{ __html: content.toString() }} />
      </PortalTooltip>

      {/* Mobile bottom sheet via portal */}
      {isOpen &&
        createPortal(
          <div
            className={classNames(styles.overlay, {
              [styles.overlay_closing]: isClosing,
            })}
            role="dialog"
            aria-modal="true"
            onClick={closeSheet}
          >
            <div
              className={classNames(styles.sheet, {
                [styles.sheet_closing]: isClosing,
              })}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.sheet_header}>
                <div className={styles.sheet_titleRow}>
                  {title && (
                    <span className={styles.sheet_title}>{title}</span>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.sheet_closeBtn}
                  onClick={closeSheet}
                >
                  <i className="pi pi-times" />
                </button>
              </div>
              <div className={styles.sheet_content}>{renderedContent}</div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
